import Logger from '@/logger';
import { reportForensicEvent } from '@/forensicEvents';

const logger = new Logger({screen: 'GraphQLUtils'});

let client = null;

export function initializeClient(appClient) {
  client = appClient;
}

export const fetchRestaurantDoc = /* GraphQL */ `
  query Query {
    currentDevice {
      tag
      deviceSettings {
        parameter
        value
      }
      restaurant {
        id
        client
        clientByClient {
          language
        }
        name
        proActivated
      }
    }
  }
`;

/**
 * What the restaurant query's answer says about this device, once the call itself succeeded.
 *  - 'ok'             the device row and its restaurant came back.
 *  - 'device_missing' `currentDevice` is null: the signed-in username has no row in bronze.device. The
 *                     token is still valid (Cognito access tokens last a day), but the id was renamed or
 *                     deleted, so nothing this session asks for will ever come back.
 *  - 'no_restaurant'  the device row exists but is attached to no restaurant (configuration, not session).
 * A thrown call (timeout, network, AppSync error) never reaches this: that is a failure, not an answer.
 */
export function classifyCurrentDevice(data) {
  const device = data?.currentDevice;
  if (!device) return 'device_missing';
  return device.restaurant ? 'ok' : 'no_restaurant';
}

// Lightweight query for the hot path: re-read the device config on a `reconfigure` push without
// re-fetching the restaurant (which would re-trigger the realtime channel watcher in App.vue).
// `deviceSettings` is a plain list (this API exposes simple collections, not Relay connections).
export const fetchDeviceSettingsDoc = /* GraphQL */ `
  query DeviceSettings {
    currentDevice {
      deviceSettings {
        parameter
        value
      }
    }
  }
`;

// Persist a single device setting from the tablet via the server-side function
// bronze.set_device_setting(_parameter, _value). The function resolves device_id from the AppSync
// session identity and forces updated_from_device = true + updated_at = now() server-side, so the
// tablet never sends the device id or the echo guard by hand (and a tablet still can't trigger a
// reconfigure of itself). This is now the ONLY write path — the direct CRUD UPDATE grant on
// device_setting was revoked. The function returns the device_setting row, so the payload is of
// type DeviceSetting (select the columns directly, not a Payload wrapper).
export const setDeviceSettingMutation = /* GraphQL */ `
  mutation Mutation($input: SetDeviceSettingInput!) {
    setDeviceSetting(input: $input) {
      parameter
      value
    }
  }
`;

// `recipientName` (customer name) and `recipientAddress` (delivery address) are fetched for ALL
// clients but only rendered inside the order button for the RBE pilot, gated by the `rbe-order-button`
// PostHog flag in OrderSelectionComponent. Within the flag the field shown is hardcoded per client:
// Popeyes shows the name, Burger King shows the address (they operate by address, not by name).
// Both are customer PII: display-only on the device, never sent to PostHog.
// Backend contract: exposed on RealtimeOrderPosRawData in bronze-tracker-api-v3 (see Eric handoff).
// The backend (bronze-tracker-api-v3) is deployed and populates recipientAddress; like recipientName
// it also arrives on the AppSync new_order / order_update events (captured by the `...data.event`
// spread), so the whole front flow — fetch, realtime create/update, IndexedDB, render — is covered.
export const fetchRawOrdersDoc = /* GraphQL */ `
  query Query {
    getRawOrdersRealtime {
      id
      posId
      deliveryId
      processedAt
      createdAt
      ticketId
      brand
      brandByBrand {
        name
      }
      deliveryPlatform
      recipientName
      recipientAddress
      orderId
      order {
        hasPhoto
        manualIsCorrect
        photos {
          url
        }
        orderAnalyses(first: 1, orderBy: CREATED_AT_DESC) {
          id
          isCorrect
          appResponse
          createdAt
          updatedAt
        }
        allergies
        comment
        orderDigitalTicket {
          grouped
          hierarchical
        }
        orderEvents {
          registeredAt
          event
        }
      }
    }
  }
`;

// The same document with the backend's windows widened (the function defaults are 60 min of
// creation age and 30 min since an order left the active list). Used ONLY by the one-off lookup
// behind "this manual order already exists" (src/manualDuplicate.js), never by the regular fetch or
// any poll. BOTH arguments are needed: an order that is already READY comes back by the function's
// "recently changed status" arm, which needs its own window as well as the creation one. Derived
// from the regular document so the two can never drift apart in what they select.
export const WIDE_LOOKUP_MAX_AGE_MINUTES = 240;
export const fetchRawOrdersWideDoc = fetchRawOrdersDoc.replace(
  'getRawOrdersRealtime {',
  `getRawOrdersRealtime(maxAgeMinutes: ${WIDE_LOOKUP_MAX_AGE_MINUTES}, maxAgeMinutesForOtherStatuses: ${WIDE_LOOKUP_MAX_AGE_MINUTES}) {`,
);

// One order by id, with the fields the tablet renders (the same ones `getRawOrdersRealtime` selects
// under `order`). Devices may SELECT their own restaurant's orders under RLS (the help flow already
// reads them with OrderPhotosById). Used only to open the order the backend NAMED in an "already
// exists" answer, which is exact and does not depend on how old the order is.
export const fetchOrderForDuplicateDoc = /* GraphQL */ `
  query ManualDuplicateOrderById($id: String!) {
    order(id: $id) {
      id
      ticketId
      deliveryId
      deliveryPlatform
      createdAt
      posProviderId
      hasPhoto
      manualIsCorrect
      allergies
      comment
      photos {
        url
      }
      orderAnalyses(first: 1, orderBy: CREATED_AT_DESC) {
        id
        isCorrect
        appResponse
        createdAt
        updatedAt
      }
      orderDigitalTicket {
        grouped
        hierarchical
      }
      orderEvents {
        registeredAt
        event
      }
    }
  }
`;

// The photos of orders that already left the screen, for the help flow's "Quiero ver las fotos de
// un pedido" (src/orderPhotos.js). No new backend: devices may SELECT `order`, `photo` and
// `order_analysis` of their own restaurant (RLS policies order_access_by_account_or_device,
// photo_select_policy, order_analysis_select_policy), and `photo.url` is a public S3 object, so
// an <img> loads it as-is — the Hub renders the same URLs.
// `restaurantId` and `isDelivery` are not redundant with RLS: they are what lets Postgres walk
// idx_order_rest_created_deliv_true (restaurant_id, created_at DESC) WHERE is_delivery, 0.1 ms
// on the busiest photographed store (EXPLAIN ANALYZE, sep-2026). Without them the scan is the
// whole restaurant. Every order a tablet ever lists is a delivery one (the realtime mirror only
// carries is_delivery rows), so the condition loses nothing.
const ORDER_PHOTOS_FIELDS = `
      id
      ticketId
      deliveryId
      posProviderId
      deliveryPlatform
      createdAt
      manualIsCorrect
      isManualInsert
      photos(orderBy: [CREATED_AT_ASC]) {
        url
        createdAt
      }
      orderAnalyses(first: 1, orderBy: [CREATED_AT_DESC]) {
        id
        isCorrect
        appResponse
      }
`;

export const fetchRecentOrderPhotosDoc = /* GraphQL */ `
  query RecentOrderPhotos($restaurantId: String!, $first: Int!, $offset: Int!) {
    orders(
      condition: { restaurantId: $restaurantId, isDelivery: true, hasPhoto: true }
      orderBy: [CREATED_AT_DESC]
      first: $first
      offset: $offset
    ) {${ORDER_PHOTOS_FIELDS}    }
  }
`;

// "Un pedido salió sin foto": the same page as RecentOrderPhotos, of the orders that never got one.
// Same field and condition type as the photo list, so no schema change; `photos` comes back empty.
export const fetchRecentOrdersWithoutPhotoDoc = /* GraphQL */ `
  query RecentOrdersWithoutPhoto($restaurantId: String!, $first: Int!, $offset: Int!) {
    orders(
      condition: { restaurantId: $restaurantId, isDelivery: true, hasPhoto: false }
      orderBy: [CREATED_AT_DESC]
      first: $first
      offset: $offset
    ) {${ORDER_PHOTOS_FIELDS}    }
  }
`;

export const fetchOrderPhotosDoc = /* GraphQL */ `
  query OrderPhotosById($id: String!) {
    order(id: $id) {${ORDER_PHOTOS_FIELDS}    }
  }
`;

export const fetchCrossedOutItemsQuery = /* GraphQL */ `
  query GetRawOrderDetailsContentCross($sellId: String!, $tag: String!) {
    rawOrderDetailsContentCross(sellId: $sellId, tag: $tag) {
      sellId
      tag
      crossedOutAt
      crossedOutBy
    }
  }
`;

export const createTriggerMutation = /* GraphQL */ `
  mutation Mutation($input: CreateS3MetadataTriggerInput!) {
    createS3MetadataTrigger(input: $input) {
      imageKey
    }
  }`;

export const createAnalysisHistoryMutation = /* GraphQL */ `
  mutation Mutation($input: CreateAnalysisHistoryInput!) {
    createAnalysisHistory(input: $input) {
      analysisId
    }
  }`;

export const createCrossItemMutation = /* GraphQL */ `
  mutation Mutation($input: CreateRawOrderDetailsContentCrossInput!) {
    createRawOrderDetailsContentCross(input: $input) {
      sellId
      tag
      crossedOutBy
    }
  }
`;

export const updateOrderAnalysisMutation = /* GraphQL */ `
  mutation Mutation($input: UpdateOrderAnalysisInput!) {
    updateOrderAnalysis(input: $input) {
      appResponse
      isCorrect
      description
      updatedAt
      processingTime
    }
  }
`;

// Writes a RIDER_LEFT_THE_RESTAURANT order_event when the operator hands a rider-order to the rider.
// Gated server-side by RLS (public_api may only INSERT event='RIDER_LEFT_THE_RESTAURANT' on orders of
// its own restaurant) and the (order_id, event) PK makes it idempotent-ish — a duplicate raises, which
// registerRiderLeft() below swallows. registeredAt is forced server-side (the device can't set it).
export const createOrderEventMutation = /* GraphQL */ `
  mutation Mutation($input: CreateOrderEventInput!) {
    createOrderEvent(input: $input) {
      orderId
      event
    }
  }
`;

export const updateLastAliveMutation = /* GraphQL */ `
  mutation Mutation($input: SyncDeviceInput!) {
    syncDevice(input: $input) {
      id
      appVersion
      lastAlive
      restaurantId
      ordersMaxSeq
      ordersMaxSeqAgeSeconds
    }
  }
`;

export const updateCrossItemMutation = /* GraphQL */ `
  mutation Mutation($input: UpdateRawOrderDetailsContentCrossInput!) {
    updateRawOrderDetailsContentCross(input: $input) {
      sellId
      tag
      crossedOutBy
    }
  }
`;

export const updateOrderMutation = /* GraphQL */ `
  mutation Mutation($input: UpdateOrderInput!) {
    updateOrder(input: $input) {
      manualIsCorrect
    }
  }
`;

export async function triggerMutation(order, appVersion, photoKey, restaurantId) {
    /**
     * Triggers a GraphQL mutation to create an S3MetadataTrigger. This trigger is used to launch the AWS Step Function
     * execution that processes the photo.
     * @param {object} order - The order object.
     * @param {string} appVersion - The version of the app running on the device.
     * @param {string} photoKey - The key of the photo to process.
     * @param {string} restaurantId - The ID of the restaurant.
     */

    logger.info(`Photo Key: ${photoKey} - Creating s3MetadataTrigger entry`, {
        photoKey: photoKey,
        restaurantId: restaurantId,
        orderId: order.orderId || null,
        deliveryId: order.deliveryId || null,
        ticketId: order.ticketId || null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        orderAnalysis: order.orderAnalysis || null,
    })
    
    const variables = {
        input: {
            s3MetadataTrigger: {
                bucketName: 'bronze-tracker',
                appVersion: appVersion,
                orderId: order.orderId || null,
                deliveryId: order.deliveryId || null,
                imageKey: photoKey,
                orderAnalysisId: order.orderAnalysis || null,
                restaurantId: restaurantId,
                tag: 'Default',
                ticketId: order.ticketId || null,
                weightKg: null,
            }
        }
    };

    const result = await executeGraphQLQuery(createTriggerMutation, variables);

    if (!result.createS3MetadataTrigger) {
        logger.error(`Photo Key: ${photoKey} - Failed to create s3MetadataTrigger entry.`, {result})
    }
    else{
        logger.success(`Photo Key: ${photoKey} - s3MetadataTrigger entry created.`)
    }
}

export async function updateAppResponseResults(orderAnalysisID, analysisResult) {
    /**
     Function to update everything related to the analysis result fields, i.e. appResponse, isCorrect, description,
     processingTime.
     It calls the createAnalysisHistory mutation to create an entry in the AnalysisHistory table and the updateOrderAnalysis
     mutation to update the OrderAnalysis table.

     * @param {string} orderAnalysisID - The ID of the order analysis to update.
     * @param {object} analysisResult - The result of the analysis. It should contain the app_response, is_correct,
     * processingTime and description fields.
     */

    // Update the OrderAnalysis table
    await updateOrderAnalysis(orderAnalysisID, analysisResult);
    // Create an entry in the AnalysisHistory table
    await createAnalysisHistory(orderAnalysisID, analysisResult);
}

export async function createAnalysisHistory(orderAnalysisID, analysisResult) {
    /**
     Creates an entry in the AnalysisHistory table based on the result of the analysis.
     * @param {string} orderAnalysisID - The ID of the order analysis to update.
     * @param {object} analysisResult - The result of the analysis. It should contain the app_response, is_correct,
     * processingTime and description fields.
     */

    logger.info(`Analisis ID: ${orderAnalysisID} - Creating entry in AnalysisHistory`, {
        analysisId: orderAnalysisID,
        appResponse: analysisResult.appResponse,
        isCorrect: analysisResult.isCorrect,
        description: analysisResult.description,
        createdAt: new Date().toISOString(),
    });

    // Build the variables object for the GraphQL query
    const variables = {
        input: {
            analysisHistory: {
                analysisId: orderAnalysisID,
                appResponse: analysisResult.appResponse,
                isCorrect: analysisResult.isCorrect,
                description: analysisResult.description,
                createdAt: new Date().toISOString(),
            }
        }
    };

    // Execute the GraphQL query
    const result = await executeGraphQLQuery(createAnalysisHistoryMutation, variables);
    
    if (!result.createAnalysisHistory) {
        logger.error(`Analisis ID: ${orderAnalysisID} - Failed to create AnalysisHistory entry.`, {result});
    }
    else{
        logger.success(`Analisis ID: ${orderAnalysisID} - AnalysisHistory entry created.`)
    }
}

export async function updateOrderAnalysis(orderAnalysisID, analysisResult) {
    /**
     Updates the appResponse field of an order analysis.
     * @param {string} orderAnalysisID - The ID of the order analysis to update.
     * @param {object} analysisResult - The result of the analysis. It should contain the app_response, is_correct,
     * processingTime and description fields.
     */

    logger.info(`Analisis ID: ${orderAnalysisID} - Updating Order Analysis`,
        {
            appResponse: analysisResult.appResponse,
            isCorrect: analysisResult.isCorrect,
            description: analysisResult.description,
            processingTime: analysisResult.processingTime,
            updatedAt: new Date().toISOString(),
        })

    // Build the variables object for the GraphQL query
    const variables = {
        input: {
            id: orderAnalysisID,
            patch: {
                appResponse: analysisResult.appResponse,
                isCorrect: analysisResult.isCorrect,
                description: analysisResult.description,
                processingTime: analysisResult.processingTime,
                updatedAt: new Date().toISOString(),
            }
        }
    };

    // Execute the GraphQL query
    const result = await executeGraphQLQuery(updateOrderAnalysisMutation, variables);

    if (!result.updateOrderAnalysis) {
        logger.error(`Analisis ID: ${orderAnalysisID} - Failed to update Order Analysis.`, {result});
    } else {
        logger.success(`Analisis ID: ${orderAnalysisID} - Order Analysis updated successfully.`);
    }
}

export async function updateLastAlive(appVersion) {
    /**
     * Updates the lastAlive field of a device.
     * @param {string} appVersion - The version of the app running on the device.
     * @returns {Promise<object|null>} The synced device, or null when the server returned none.
     */

    logger.console('Updating last alive', {
        appVersion: appVersion,
        lastAlive: new Date().toISOString(),
    })

    // Build the variables object for the GraphQL query
    const variables = {
        input: {
              _appVersion: appVersion,
              _lastAlive: new Date().toISOString()
            }
    };

    // Execute the GraphQL query
    const result = await executeGraphQLQuery(updateLastAliveMutation, variables);

    if (!result.syncDevice) {
        logger.error(`Failed to update lastAlive.`, {result});
    } else {
        logger.console(`lastAlive updated successfully.`);
    }
    // The device as the server sees it: ordersMaxSeq / ordersMaxSeqAgeSeconds feed the seq hint (seqHint.js).
    return result.syncDevice ?? null;
}

export async function fetchCrossedOutItems(sellId, tag) {
    /**
     * Fetches a crossed out item from the database.
     * @param {string} sellId - The ID of the item.
     * @param {string} tag - The tag for the cross item.
     */

    const variables = {
      sellId: sellId,
      tag: tag
    };
    logger.console(variables)

    const result = await executeGraphQLQuery(fetchCrossedOutItemsQuery, variables);

    if (!result.rawOrderDetailsContentCross) {
        return false;
    } else {
        return true;
    }
}

export async function createCrossItem(sellId, tag, crossedOutBy) {
    /**
     * Creates a cross item in the database.
     * @param {string} sellId - The ID of the item.
     * @param {string} tag - The tag for the cross item.
     * @param {string} crossedOutBy - Who crossed out the item: MANUAL or AUTO
     */

    const variables = {
      input: {
          rawOrderDetailsContentCross: {
              sellId: sellId,
              tag: tag,
              crossedOutBy: crossedOutBy
          }
      }
  };

    const result = await executeGraphQLQuery(createCrossItemMutation, variables);

    if (!result.createRawOrderDetailsContentCross) {
        logger.error(`Failed to create cross item.`, {result});
    } else {
        logger.success(`Cross item created successfully.`);
    }
}

export async function registerRiderLeft(orderId) {
    /**
     * Persists the operator's "handed to the rider" tap as a RIDER_LEFT_THE_RESTAURANT order_event.
     * This is what closes a own-delivery rider order server-side: get_raw_orders_realtime's rider arm
     * anti-joins RIDER_LEFT, so the backend stops returning the order (no more resurrection after a
     * reload), and the DB trigger fans an order_update out to the restaurant's other tablets so they
     * hide it too. It also feeds the own-delivery dwell metric (RIDER_CHECK_IN -> RIDER_LEFT).
     * Fire-and-forget: the card is already hidden optimistically, so any failure (network, or the
     * (order_id, event) PK on a double-write) is non-fatal — we just log and move on.
     * @param {string} orderId - bronze.order.id of the delivered order.
     * @returns {Promise<boolean>} true if the event was written.
     */
    return (await recordRiderLeft(orderId)) === 'ok';
}

// What Postgres says when the (order_id, event) primary key refuses a second RIDER_LEFT. Amplify throws
// either an Error or the whole {data, errors} result, so both shapes are read.
const isDuplicateEventError = (error) => {
    const messages = [error?.message, ...(Array.isArray(error?.errors) ? error.errors.map((e) => e?.message) : [])];
    return messages.some((m) => /duplicate key|unique constraint/i.test(String(m || '')));
};

export async function recordRiderLeft(orderId, { attempts = 1, delayMs = 3000 } = {}) {
    /**
     * The same RIDER_LEFT_THE_RESTAURANT write as registerRiderLeft, saying how it went: 'ok' (written),
     * 'duplicate' (the order was already handed over) or 'failed'.
     * The QR gun closes the order with it and retries a network failure, since there the scan is the
     * only signal that the rider took the bag. An expired session is not retried: it does not go away by
     * waiting. Never rejects.
     * The real backend answers a second write of the same (order, event) with `createOrderEvent: null`
     * and no error, and writes nothing (bronze-burgers, 27-oct-2026: one row, the rescan answered null).
     * That answer is 'duplicate', not 'failed': a «Sin guardar» there was a false alarm, retried for
     * nothing on every reconnection. The PK's own error is still read as 'duplicate' too.
     * @param {string} orderId - bronze.order.id of the delivered order.
     * @returns {Promise<'ok'|'duplicate'|'failed'>}
     */
    if (!orderId) return 'failed';
    const variables = {
        input: {
            orderEvent: {
                orderId: orderId,
                event: 'RIDER_LEFT_THE_RESTAURANT'
            }
        }
    };
    for (let attempt = 1; attempt <= attempts; attempt++) {
        try {
            const result = await executeGraphQLQuery(createOrderEventMutation, variables);
            if (result?.createOrderEvent) {
                logger.success(`RIDER_LEFT_THE_RESTAURANT registered for order ${orderId}`);
                return 'ok';
            }
            if (result && 'createOrderEvent' in result && result.createOrderEvent === null) {
                logger.info(`RIDER_LEFT_THE_RESTAURANT already registered for order ${orderId} (null answer)`);
                return 'duplicate';
            }
            logger.warning(`registerRiderLeft: no event returned for order ${orderId}`, {result});
            return 'failed';
        } catch (error) {
            if (isDuplicateEventError(error)) {
                logger.info(`RIDER_LEFT_THE_RESTAURANT already registered for order ${orderId}`);
                return 'duplicate';
            }
            if (isAuthError(error)) return 'failed';
            // Transient error — the card is already hidden locally.
            logger.warning(`registerRiderLeft failed for order ${orderId} (attempt ${attempt}/${attempts}, non-fatal)`, {error});
        }
        if (attempt < attempts) await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
    return 'failed';
}

export async function updateCrossItem(sellId, tag, crossedOutBy) {
    /**
     * Updates a cross item in the database.
     * @param {string} sellId - The ID of the item.
     * @param {string} tag - The tag for the cross item.
     * @param {string} crossedOutBy - Who crossed out the item.
     */
    
    const variables = {
        input: {
          sellId: sellId,
          tag: tag,
          patch: {
              crossedOutBy: crossedOutBy
          }
        }
    }
    logger.console(variables)

    const result = await executeGraphQLQuery(updateCrossItemMutation, variables);

    if (!result.updateRawOrderDetailsContentCross) {
        logger.error(`Failed to update cross item.`, {result});
    } else {
        logger.success(`Cross item updated successfully.`);
    }
}

export async function createOrUpdateCrossItem(sellId, tag, crossedOutBy) {
    /**
     * Creates or updates a cross item in the database depending on whether it already exists.
     * @param {string} sellId - The ID of the sell.
     * @param {string} tag - The tag for the cross item.
     * @param {string} crossedOutBy - Who crossed out the item.
     */
    try {
        // First, get all crossed out items to check if this one exists
        const result = await fetchCrossedOutItems(sellId, tag);
        
        // Check if we have results and if the sellId exists in the crossed out items
        if (result) {
            // Item exists, update it
            logger.console(`Item with sellId ${sellId} exists, updating it`);
            return updateCrossItem(sellId, tag, crossedOutBy);
        } else {
            // Item doesn't exist, create it
            logger.console(`Item with sellId ${sellId} doesn't exist, creating new`);
            return createCrossItem(sellId, tag, crossedOutBy);
        }
    } catch (error) {
        logger.error(`Error in createOrUpdateCrossItem for sellId: ${sellId}`, error);
        throw error;
    }
}

export async function updateOrderAsCorrect(orderId) {
    /**
     * Marks an order as manually correct by updating the manualIsCorrect field to true.
     * @param {string} orderId - The ID of the order to mark as correct.
     */
    
    logger.info(`Order ID: ${orderId} - Marking order as manually correct`, {
        orderId: orderId,
        manualIsCorrect: true,
        updatedAt: new Date().toISOString(),
    });

    const variables = {
        input: {
            id: orderId,
            patch: {
                manualIsCorrect: true
            }
        }
    };

    const result = await executeGraphQLQuery(updateOrderMutation, variables);

    if (!result.updateOrder) {
        logger.error(`Order ID: ${orderId} - Failed to mark order as manually correct.`, {result});
    } else {
        logger.success(`Order ID: ${orderId} - Order marked as manually correct successfully.`);
    }

    return result;
}

export async function setDeviceSetting(parameter, value) {
    /**
     * Persists a single camera setting changed on the tablet via the server-side function
     * bronze.set_device_setting. The device id, the echo guard (updated_from_device = true) and
     * updated_at are all resolved/forced server-side from the session identity — the tablet only
     * sends the parameter and its value.
     * @param {string} parameter - The DB parameter name (snake_case, e.g. 'photo_quality').
     * @param {string|null} value - The varchar value to store; null means "use the default".
     */

    // PostGraphile exposes the SQL function args (_parameter, _value) with their leading underscores
    // preserved as the input field names — verified against the deployed schema (the plain
    // `{ parameter, value }` from the task doc is rejected by SetDeviceSettingInput).
    const variables = {
        input: {
            _parameter: parameter,
            _value: value,                          // varchar; null for "Automatic"/reset
        }
    };

    const result = await executeGraphQLQuery(setDeviceSettingMutation, variables);

    if (!result.setDeviceSetting) {
        logger.error(`Failed to persist device setting '${parameter}'.`, { result, parameter, value });
    } else {
        logger.console(`Device setting '${parameter}' persisted as '${value}'.`);
    }

    return result;
}

// Missed-event recovery for the realtime ledger (bronze.events_since). Returns the events of a
// channel AFTER the given cursor, oldest first, capped at 100 rows server-side — exactly 100
// returned means "call again with the new cursor" (eventLedger.js paginates on that). RLS limits
// the visible channels to the ones this device may subscribe to, so a wrong channel argument
// returns [] rather than erroring. Contract verified against the deployed schema:
// eventsSince(pChannel: String, pCursor: Int): [RealtimeEvent] — seq/pCursor are Int (not
// BigInt: per-channel seqs stay far below 2^31), payload is AWSJSON (arrives as a JSON string).
export const eventsSinceDoc = /* GraphQL */ `
  query EventsSince($pChannel: String!, $pCursor: Int!) {
    eventsSince(pChannel: $pChannel, pCursor: $pCursor) {
      eventId
      seq
      type
      payload
      createdAt
    }
  }
`;

// Cursor seeding for the event ledger: the channel's current max seq, WITHOUT fetching
// (let alone applying) any event. A device that has never received a live event has no
// cursor, and resync deliberately skips cursorless channels (it would replay up to 72 h
// of history) — so anything published during a disconnection that happens BEFORE the
// first live push would be unrecoverable. Seeding at channel init closes that window.
export const channelMaxSeqDoc = /* GraphQL */ `
  query ChannelMaxSeq($channel: String!) {
    realtimeEvents(condition: { channel: $channel }, orderBy: [SEQ_DESC], first: 1) {
      seq
    }
  }
`;

export async function fetchChannelMaxSeq(channel) {
    /**
     * Current max seq of a ledger channel, or 0 when the channel has no events (a 0 cursor
     * is safe: the next event is seq 1 == cursor+1, and eventsSince(0) on an empty channel
     * returns nothing).
     * @param {string} channel - Channel path without leading slash.
     */
    const data = await executeGraphQLQuery(channelMaxSeqDoc, { channel });
    const rows = data?.realtimeEvents || [];
    return rows.length > 0 ? Number(rows[0].seq) : 0;
}

export async function fetchEventsSince(channel, cursor) {
    /**
     * Fetches the ledger events of a channel after the given cursor (missed-event recovery).
     * @param {string} channel - Channel path WITHOUT leading slash (e.g. 'orders/<restaurantId>')
     *                           — same shape as the subscribe paths and the ledger's channel column.
     * @param {number} cursor - Last contiguously-applied seq; only events with seq > cursor return.
     * @returns {Array<{eventId: string, seq: number, type: string|null, payload: object, createdAt: string}>}
     */
    const data = await executeGraphQLQuery(eventsSinceDoc, {
        pChannel: channel,
        pCursor: Number(cursor),
    });
    const rows = data?.eventsSince || [];
    return mapEventRows(rows);
}

// Same wire shape as eventsSince, but anchored on the ledger's send-time instead of a
// seq cursor. It exists because a seq anchor has to be captured BEFORE a disconnection
// (fetchChannelMaxSeq at boot can fail/race, leaving a channel cursorless and its
// reconnect resync a silent no-op), whereas a device-clock timestamp captured at boot
// cannot fail. Used only to BOOTSTRAP a cursorless channel: the first page's max seq
// becomes the cursor and the ledger continues with the normal seq-based paging above.
// bronze.events_since_time(channel, since) mirrors events_since: SETOF realtime_event,
// SQL STABLE, INVOKER (device-only RLS applies), ORDER BY seq LIMIT 100.
export const eventsSinceTimeDoc = /* GraphQL */ `
  query EventsSinceTime($pChannel: String!, $pSince: AWSDateTime!) {
    eventsSinceTime(pChannel: $pChannel, pSince: $pSince) {
      eventId
      seq
      type
      payload
      createdAt
    }
  }
`;

function mapEventRows(rows) {
    return rows.map((row) => ({
        eventId: row.eventId,
        seq: Number(row.seq),
        type: row.type,
        payload: parseEventPayload(row),
        createdAt: row.createdAt,
    }));
}

/**
 * A ledger row's payload, or `{}` when it cannot be parsed — never a throw.
 *
 * payload is a JSON scalar: PostGraphile may deliver it as an object or as a JSON string.
 * Parsing it unguarded threw out of fetchEventsSince, and resyncChannel catches that as
 * "events_since unreachable" and arms an exponential backoff — which refetches the SAME
 * poison row forever. The channel never recovers, and the log says "will retry", so the
 * tablet goes deaf with no socket error to find and the failure reads as connectivity.
 *
 * The row is KEPT rather than dropped, and that is the whole design: resyncChannel derives
 * three things from the row list that a missing row silently breaks —
 *   advanceCursor(row.seq)          a dropped last-of-page row leaves the cursor before it,
 *                                   so the next fetch returns the same poison row again;
 *   enqueueAck(row.eventId)         a dropped row is never acked and stays in the ledger;
 *   while (page.length === PAGE_SIZE) a short page ends pagination early and silently
 *                                   truncates the recovery.
 * Dropping would trade a loud permanent hang for a quiet one. Neutralising the payload keeps
 * every one of those invariants and costs only one event's contents — which the orders
 * channel refetches wholesale anyway (coalesceResync).
 */
function parseEventPayload(row) {
    if (typeof row.payload !== 'string') return row.payload || {};
    try {
        return JSON.parse(row.payload);
    } catch (error) {
        logger.error('Unparseable ledger payload, continuing with an empty one', {
            eventId: row.eventId, seq: row.seq, type: row.type, error,
        });
        reportForensicEvent('ledger_payload_unparseable', {
            event_id: row.eventId ?? null,
            seq: Number(row.seq) ?? null,
            event_type: row.type ?? null,
        });
        return {};
    }
}

export async function fetchEventsSinceTime(channel, since) {
    /**
     * First page of a channel's ledger events with created_at > `since` (cursorless
     * bootstrap). Order/shape identical to fetchEventsSince so the ledger can seamlessly
     * switch to seq paging after this call.
     * @param {string} channel - Channel path WITHOUT leading slash.
     * @param {string} since - ISO 8601 timestamp; only events created strictly after it return.
     */
    const data = await executeGraphQLQuery(eventsSinceTimeDoc, {
        pChannel: channel,
        pSince: since,
    });
    const rows = data?.eventsSinceTime || [];
    return mapEventRows(rows);
}

// Own-delivery code presets for the current device's restaurant, resolved server-side from the
// session. PostGraphile exposes it as a `JSON` scalar that arrives as a JSON STRING, e.g.
//   {"prepends":["276","260622"],"appends":[],"regex":"\\b\\d{12}\\b","expiration":"…Z"}
// for KFC restaurants WITH own delivery; `null` when the restaurant has none (e.g. KFC FR stores
// that only do Uber/Deliveroo). The tablet uses it to (a) decide whether to offer OWN_DELIVERY in
// the manual-order selector and (b) compose the code (prepends + typed suffix + appends) with the
// date computed server-side (Europe/Madrid) so it can never go stale.
export const fetchOwnDeliveryCodePresetsDoc = /* GraphQL */ `
  query OwnDeliveryCodePresets {
    ownDeliveryCodePresets
  }
`;

// ── Device-scoped TTL caches for the manual-order selector ──────────────────────────────────────
// The manual-order modal is opened on every workflow turn; re-querying the backend on each open
// froze the flow for ~1-2s. We cache per device session: the platform list changes very rarely
// (franchise config) so it gets a flat TTL; the own-delivery presets carry their own `expiration`
// (midnight Madrid) so the composed date never goes stale — we honour it, with a short fallback.
// Both caches are keyed by the stored device_id, so a device switch (even without a page reload)
// is a cache miss and refetches.
const PLATFORMS_TTL_MS = 30 * 60 * 1000;
const OWN_DELIVERY_FALLBACK_TTL_MS = 5 * 60 * 1000;
let _platformsCache = null;     // { deviceId, data, expiresAt }
let _ownDeliveryCache = null;   // { deviceId, data, expiresAt }

function currentDeviceId() {
    try { return localStorage.getItem('device_id') || null; } catch (e) { return null; }
}

export async function fetchOwnDeliveryCodePresets({ force = false } = {}) {
    const deviceId = currentDeviceId();
    const now = Date.now();
    if (!force && _ownDeliveryCache && _ownDeliveryCache.deviceId === deviceId && _ownDeliveryCache.expiresAt > now) {
        return _ownDeliveryCache.data;
    }
    const result = await executeGraphQLQuery(fetchOwnDeliveryCodePresetsDoc);
    let val = result?.ownDeliveryCodePresets;
    let data = null;
    if (val != null) {
        // The JSON scalar arrives double-encoded (a JSON string of a JSON string), so parse until
        // we reach the object (max 2 passes).
        try {
            for (let i = 0; i < 2 && typeof val === 'string'; i++) {
                val = JSON.parse(val);
            }
            data = (val && typeof val === 'object') ? val : null;
        } catch (error) {
            logger.error('Failed to parse ownDeliveryCodePresets', { val, error });
            data = null;
        }
    }
    // Cache until the backend-provided expiration (midnight Madrid) so the composed own-delivery
    // date can never go stale; fall back to a short TTL when absent/unparseable.
    let expiresAt = now + OWN_DELIVERY_FALLBACK_TTL_MS;
    const exp = data && data.expiration ? Date.parse(data.expiration) : NaN;
    if (!Number.isNaN(exp) && exp > now) expiresAt = exp;
    _ownDeliveryCache = { deviceId, data, expiresAt };
    return data;
}

// Delivery platforms configured for the calling tablet's restaurant, resolved server-side from the
// device session AND franchise inheritance (the function walks the client parent chain and, per
// platform, keeps the nearest ancestor's row — closest override wins). This is the authoritative
// source for the manual-order selector: the list of platforms AND, per platform, the regexes for
// the codes involved.
//
// IMPORTANT — two different regexes, and the selector validates against the RIGHT one:
//   • deliveryPlatformByDeliveryPlatform.deliveryIdRegex (global, `delivery_platform`): the
//     platform's delivery_id shape — Uber `\b[A-F0-9]{5}\b` (5 hex), Glovo `\b\d{12}\b`,
//     JustEat `\b\d{9}\b`. A manual order is STORED as a delivery_id and the backend
//     (WEBAPP-API-create_order) matches it by EXACT delivery_id, so THIS is what the operator
//     types and what the selector validates for aggregator platforms.
//   • ticketIdRegex (per client, `delivery_platform_client`): the short "pickup code" printed on
//     the kitchen ticket (KFC = `\b\d{3}\b`), used by the backend for OCR/photo matching — NOT the
//     order's delivery_id. The selector only falls back to it for channels whose deliveryIdRegex is
//     `(?!)` (no aggregator id: OWN_DELIVERY / TAKE_AWAY), where the code IS the internal one.
//
// Returns a PLAIN list (this PostGraphile exposes simple collections, not Relay connections — no
// `nodes`). `inherit` defaults to true server-side, so we omit it. An empty array means "no
// platforms configured for this restaurant (nor its ancestors)"; the function raises (caught here)
// when there is no device session.
export const fetchDeliveryPlatformsDoc = /* GraphQL */ `
  query DeliveryPlatforms {
    deliveryPlatformsForCurrentDevice {
      deliveryPlatform
      ticketIdRegex
      skipOrders
      client
      deliveryPlatformByDeliveryPlatform {
        deliveryIdRegex
        isExternalDelivery
      }
    }
  }
`;

export async function fetchDeliveryPlatformsForCurrentDevice({ force = false } = {}) {
    const deviceId = currentDeviceId();
    const now = Date.now();
    if (!force && _platformsCache && _platformsCache.deviceId === deviceId && _platformsCache.expiresAt > now) {
        return _platformsCache.data;
    }
    const result = await executeGraphQLQuery(fetchDeliveryPlatformsDoc);
    const list = result?.deliveryPlatformsForCurrentDevice;
    const data = Array.isArray(list) ? list : [];
    _platformsCache = { deviceId, data, expiresAt: now + PLATFORMS_TTL_MS };
    return data;
}

/**
 * The latest photographed orders of the device's restaurant, newest first by creation time.
 * @returns {Promise<object[]>} server `Order` rows (see ORDER_PHOTOS_FIELDS); throws on failure
 */
export async function fetchRecentOrderPhotos({ restaurantId, first, offset = 0 }) {
    const data = await executeGraphQLQuery(fetchRecentOrderPhotosDoc, { restaurantId, first, offset });
    return Array.isArray(data?.orders) ? data.orders : [];
}

/**
 * The latest orders of the device's restaurant that never got a photo, newest first by creation.
 * @returns {Promise<object[]>} server `Order` rows (see ORDER_PHOTOS_FIELDS); throws on failure
 */
export async function fetchRecentOrdersWithoutPhoto({ restaurantId, first, offset = 0 }) {
    const data = await executeGraphQLQuery(fetchRecentOrdersWithoutPhotoDoc, { restaurantId, first, offset });
    return Array.isArray(data?.orders) ? data.orders : [];
}

/**
 * One order with its photos — the order open in the camera, which the list may not hold yet.
 * @returns {Promise<object|null>} the server `Order`, or null when it is not visible to the device
 */
export async function fetchOrderPhotos(orderId) {
    const data = await executeGraphQLQuery(fetchOrderPhotosDoc, { id: orderId });
    return data?.order || null;
}

export async function executeGraphQLQuery(query, variables = {}) {
    /**
     * Generic function to execute a GraphQL query.
     * @param {string} query - The GraphQL query.
     * @param {object} variables - The variables for the query.
     */
    try {
        const result = await client.graphql({
            query: query, variables: variables,
        });
        return result.data;
    } catch (error) {
        if (isAuthError(error)) {
            // Expired/absent Cognito session. This is an expected condition, not a bug:
            // report it as a (throttled) warning and hand control to App.vue, which stops
            // all polling loops and shows the login screen. Without this, the 15s/30s
            // fallback loops retry forever and a single tablet can emit thousands of
            // identical errors per day.
            logger.warning('GraphQL auth session expired, requesting re-login', { error });
            window.dispatchEvent(new CustomEvent('sessionExpired'));
        } else {
            logger.error('GraphQL query error', { error });
        }
        throw error;
    }
}

// Amplify surfaces an expired/missing session under several names depending on the
// layer that failed (credentials provider, AppSync, Cognito). Treat them all as the
// same "needs re-login" condition.
const AUTH_ERROR_NAMES = ['NoValidAuthTokens', 'NoSignedUser', 'NotAuthorizedException', 'UnauthorizedException', 'NoCredentials'];
function isAuthError(error) {
    if (!error) return false;
    if (AUTH_ERROR_NAMES.includes(error.name)) return true;
    const message = String(error.message || '');
    return message.includes('No current user') || message.includes('No federated jwt');
}
